// src/components/sections/About.tsx
// Balanced About section with aligned image and content boxes

import React, { useEffect, useRef, useState } from 'react'
import ownerImage from '../../assets/images/portrait.png'

const About: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.1 }
    )

    const currentRef = sectionRef.current

    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
      observer.disconnect()
    }
  }, [])

  return (
    <section
      ref={sectionRef}
      id="about"
      className="relative bg-gradient-to-br from-ivory-hush via-white to-neutral overflow-hidden"
    >
      {/* Subtle animated background elements */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-soft-sage/5 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-lavender-mist/8 rounded-full blur-2xl animate-float-delayed" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-warm-sand/3 rounded-full blur-3xl animate-pulse" />
      </div>

      <div className="container mx-auto px-4 py-16 md:py-20 relative">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <header
            className={`text-center mb-16 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="inline-flex items-center gap-2 mb-6 px-6 py-3 bg-white/60 backdrop-blur-sm rounded-full border border-soft-sage/20">
              <div className="w-2 h-2 bg-warm-sand rounded-full animate-ping" />
              <span className="text-warm-sand font-semibold uppercase tracking-wider text-sm">
                About the Founder
              </span>
            </div>

            <p className="text-xl text-forest-slate max-w-3xl mx-auto leading-relaxed">
              Organizing isn't just something I do — it's a divine gift I'm passionate about sharing
              with others.
            </p>
          </header>

          {/* Main Content Layout - Balanced Heights */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 xl:gap-20 items-start">
            {/* Left Column - Image Section */}
            <div
              className={`transition-all duration-1000 delay-300 transform ${
                isVisible
                  ? 'opacity-100 translate-y-0 scale-100'
                  : 'opacity-0 translate-y-12 scale-95'
              }`}
            >
              <div className="space-y-8">
                {/* Main image container */}
                <div className="relative group">
                  {/* Decorative background shape */}
                  <div className="absolute inset-0 bg-gradient-to-br from-warm-sand/20 via-soft-sage/15 to-lavender-mist/25 rounded-[3rem] transform rotate-3 group-hover:rotate-6 transition-transform duration-700 blur-xl" />

                  {/* Image frame */}
                  <div className="relative bg-white p-4 rounded-[3rem] shadow-2xl group-hover:shadow-3xl transition-all duration-700 transform group-hover:-translate-y-2">
                    <div className="relative overflow-hidden rounded-[2.5rem]">
                      <img
                        src={ownerImage}
                        alt="Catalina M, Professional Organizer and Founder of Sorted & Organized by CM"
                        className="w-full aspect-[3/4] object-cover transition-all duration-700 group-hover:scale-105"
                        loading="lazy"
                        width="500"
                        height="700"
                      />

                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-forest-slate/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                    </div>
                  </div>

                  {/* Floating elements */}
                  <div className="absolute -top-8 -right-8 w-24 h-24 bg-lavender-mist/20 rounded-full blur-xl animate-float" />
                  <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-soft-sage/15 rounded-full blur-2xl animate-float-delayed" />

                  {/* Professional badge */}
                  <div className="absolute top-8 -right-6 bg-gradient-to-r from-warm-sand to-warm-sand/90 text-white px-6 py-3 rounded-2xl shadow-xl transform rotate-6 group-hover:rotate-12 transition-transform duration-500 border-4 border-white">
                    <div className="text-center">
                      <div className="font-bold text-base">Professional</div>
                      <div className="text-xs opacity-90">Organizer</div>
                    </div>
                  </div>
                </div>

                {/* Credentials/Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-soft-sage/20 hover:shadow-xl transition-shadow">
                    <div className="text-2xl font-bold text-warm-sand mb-1">10+</div>
                    <div className="text-xs text-forest-slate font-medium">Years Experience</div>
                  </div>
                  <div className="text-center p-4 bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-soft-sage/20 hover:shadow-xl transition-shadow">
                    <div className="text-2xl font-bold text-soft-sage mb-1">100+</div>
                    <div className="text-xs text-forest-slate font-medium">Happy Clients</div>
                  </div>
                  <div className="text-center p-4 bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-soft-sage/20 hover:shadow-xl transition-shadow">
                    <div className="text-2xl font-bold text-lavender-mist mb-1">5★</div>
                    <div className="text-xs text-forest-slate font-medium">Client Rating</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Content Section */}
            <div
              className={`space-y-6 transition-all duration-1000 delay-500 transform ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
            >
              {/* Personal introduction */}
              <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl border border-soft-sage/20 shadow-lg">
                <h3 className="text-2xl font-serif text-forest-slate mb-4">
                  Hi, I'm Catalina Morat{' '}
                  <span aria-label="waving hand" role="img">
                    👋
                  </span>
                </h3>
                <p className="text-base text-forest-slate leading-relaxed">
                  By day, I work in Human Resources, but beyond the office, I follow my heart into
                  organizing homes and businesses. What began as a hobby quickly became something
                  more when wonderful friends trusted me to reorganize their pantries. Their
                  encouragement lit a spark that grew into Sorted & Organized by CM — a business
                  built on purpose, passion, and the belief that clarity in our spaces brings
                  clarity to our lives.
                </p>
              </div>

              {/* Story sections - 2x2 Grid for better balance */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* My Passion */}
                <div className="bg-gradient-to-r from-soft-sage/10 to-lavender-mist/10 p-5 rounded-2xl border-l-4 border-soft-sage h-full">
                  <h4 className="font-serif text-lg text-forest-slate mb-2">My Passion</h4>
                  <p className="text-sm text-forest-slate leading-relaxed">
                    Since childhood, I've had a natural passion for bringing order and harmony to
                    spaces. There's something deeply satisfying about transforming cluttered rooms
                    into calm, functional environments.
                  </p>
                </div>

                {/* My Journey */}
                <div className="bg-gradient-to-r from-warm-sand/10 to-soft-sage/10 p-5 rounded-2xl border-l-4 border-warm-sand h-full">
                  <h4 className="font-serif text-lg text-forest-slate mb-2">My Journey</h4>
                  <p className="text-sm text-forest-slate leading-relaxed">
                    By day, I work in HR helping people grow their careers. By heart, I organize
                    homes, pantries, and offices. What started as helping friends grew into this
                    beautiful business.
                  </p>
                </div>

                {/* My Motivation */}
                <div className="bg-gradient-to-r from-dusty-plum/10 to-lavender-mist/10 p-5 rounded-2xl border-l-4 border-dusty-plum h-full">
                  <h4 className="font-serif text-lg text-forest-slate mb-2">My Motivation</h4>
                  <p className="text-sm text-forest-slate leading-relaxed">
                    One of the best parts of this journey is sharing it with my beautiful daughter,
                    who often helps me with the business. Together, we bring care, creativity, and
                    attention to detail into every project.
                  </p>
                </div>

                {/* My Mission */}
                <div className="bg-gradient-to-r from-lavender-mist/10 to-soft-sage/10 p-5 rounded-2xl border-l-4 border-lavender-mist h-full">
                  <h4 className="font-serif text-lg text-forest-slate mb-2">My Mission</h4>
                  <p className="text-sm text-forest-slate leading-relaxed">
                    Whether it's a single closet or an entire office, my goal is to help you feel
                    more at peace in your environment. When your space is organized, your mind and
                    life feel more balanced too.
                  </p>
                </div>
              </div>

              {/* Quote - Centered */}
              <div className="text-center py-6 px-8 bg-white/40 backdrop-blur-sm rounded-2xl border border-warm-sand/20">
                <p className="text-lg italic text-forest-slate/80 leading-relaxed">
                  "Thanks for stopping by — I can't wait to help you create a space that works for
                  you."
                </p>
                <p className="text-warm-sand font-semibold mt-3">— Catalina Morat</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default About
